// FFT spectrum data contract shared by the backend/simulator payload, the
// peak detector and the chart component. The repo is plain JS (no
// TypeScript), so the "type" is a JSDoc typedef plus a runtime validator.
// Validate every payload before rendering: a malformed spectrum must show an
// explicit error state, never an empty chart that looks like "no vibration".

/**
 * @typedef {('x'|'y'|'z')} FftAxis
 * @typedef {('mg'|'m/s2'|'mm/s')} FftUnit
 */

/**
 * One FFT spectrum sample for a single axis.
 * @typedef {Object} FftSpectrum
 * @property {FftAxis} axis
 * @property {number} sampleRate   Acquisition rate in Hz (Nyquist = sampleRate / 2).
 * @property {number} binCount     Number of frequency bins.
 * @property {number} binStep      Frequency width of one bin in Hz.
 * @property {FftUnit} unit        Amplitude unit shown on the Y label.
 * @property {string} timestamp    ISO-8601 instant the sample was taken.
 * @property {number[]} magnitudes Amplitude per bin, length === binCount.
 * @property {number} [rpm]        Nominal shaft speed, enables order (1x/2x/3x) labels.
 * @property {number} [bladeCount] Impeller blade count, enables BPF label.
 */

export const FFT_AXES = ['x', 'y', 'z'];
// mg (milli-g) is the Bluebox accelerometer's native amplitude unit; m/s2 and
// mm/s are kept for other sources / future velocity spectra.
export const FFT_UNITS = ['mg', 'm/s2', 'mm/s'];

const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * Validate a raw spectrum payload before rendering.
 * @param {*} payload
 * @returns {{ ok: boolean, error?: string }}
 */
export const validateSpectrum = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return { ok: false, error: 'Espectro ausente ou invalido.' };
  }
  const {
    axis,
    sampleRate,
    binCount,
    binStep,
    unit,
    timestamp,
    magnitudes,
    rpm,
    bladeCount,
  } = payload;

  if (!FFT_AXES.includes(axis)) {
    return { ok: false, error: `Eixo invalido: ${String(axis)}.` };
  }
  if (!isFiniteNumber(sampleRate) || sampleRate <= 0) {
    return { ok: false, error: 'sampleRate invalido.' };
  }
  if (!Number.isInteger(binCount) || binCount <= 0) {
    return { ok: false, error: 'binCount invalido.' };
  }
  if (!isFiniteNumber(binStep) || binStep <= 0) {
    return { ok: false, error: 'binStep invalido.' };
  }
  if (!FFT_UNITS.includes(unit)) {
    return { ok: false, error: `Unidade invalida: ${String(unit)}.` };
  }
  if (typeof timestamp !== 'string' || !timestamp) {
    return { ok: false, error: 'timestamp ausente.' };
  }
  if (!Array.isArray(magnitudes) || magnitudes.length !== binCount) {
    return { ok: false, error: 'magnitudes com tamanho diferente de binCount.' };
  }
  for (let i = 0; i < magnitudes.length; i += 1) {
    if (!isFiniteNumber(magnitudes[i])) {
      return { ok: false, error: `magnitude nao finita no bin ${i}.` };
    }
  }
  // Optional fields: only validated when present.
  if (rpm !== undefined && (!isFiniteNumber(rpm) || rpm < 0)) {
    return { ok: false, error: 'rpm invalido.' };
  }
  if (
    bladeCount !== undefined &&
    (!Number.isInteger(bladeCount) || bladeCount < 0)
  ) {
    return { ok: false, error: 'bladeCount invalido.' };
  }
  return { ok: true };
};
